/**
 * Cart Modal Component
 * Displays cart items and handles order submission
 * Wrapped with React.memo for performance
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CartItem, Establishment } from '../../types/menu';
import { API_URL } from '../../config/api';
import { useToast } from '../ui/Toast';

interface CartModalProps {
    cart: CartItem[];
    establishment: Establishment | null;
    tableId: string;
    onClose: () => void;
    onUpdateQuantity: (lineId: string, qty: number) => void;
    onRemove: (lineId: string) => void;
}

const CartModal: React.FC<CartModalProps> = React.memo(({
    cart,
    establishment,
    tableId,
    onClose,
    onUpdateQuantity,
    onRemove
}) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [customerName, setCustomerName] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [nameError, setNameError] = useState('');

    const currency = establishment?.currency || 'RWF';
    const subtotal = cart.reduce(
        (sum, item) => sum + ((item.price + Number(item.extraPricePerUnit || 0)) * item.quantity),
        0
    );
    const tax = subtotal * (Number(establishment?.tax_rate || 0) / 100);
    const service = subtotal * (Number(establishment?.service_charge || 0) / 100);
    const total = subtotal + tax + service;

    const handleSubmitOrder = async () => {
        // Clear previous errors
        setNameError('');

        if (!customerName.trim()) {
            setNameError(t('checkout.namePlaceholder', 'Please enter your name'));
            showToast(t('checkout.namePlaceholder', 'Please enter your name'), 'warning');
            return;
        }

        if (cart.length === 0) {
            showToast(t('cart.empty', 'Your cart is empty'), 'warning');
            return;
        }

        setIsSubmitting(true);

        try {
            const orderData = {
                tableId: Number(tableId),
                items: cart.map(item => {
                    const grouped: Record<number, number[]> = {};
                    (item.selectedOptions || []).forEach(sel => {
                        if (!grouped[sel.group_id]) grouped[sel.group_id] = [];
                        grouped[sel.group_id].push(sel.option_id);
                    });
                    const selected_options = Object.entries(grouped).map(([gid, oids]) => ({
                        group_id: Number(gid),
                        option_ids: oids,
                    }));
                    return {
                        product_id: item.product_id,
                        quantity: item.quantity,
                        customizations: item.customizations ? JSON.stringify(item.customizations) : null,
                        ...(selected_options.length > 0 && { selected_options }),
                    };
                }),
                customer_name: customerName,
                notes: notes
            };

            const response = await axios.post(`${API_URL}/public/orders`, orderData);

            if (response.status === 201) {
                showToast(t('cart.orderPlaced', 'Order placed successfully!') + ' ' + t('cart.orderPlacedDesc', 'Your order is being prepared.'), 'success', 5000);
                setTimeout(() => window.location.reload(), 2000);
            }
        } catch (error) {
            console.error('Error placing order:', error);
            showToast(t('cart.orderError', 'Failed to place order. Please try again.'), 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
        }}>
            <div className="glass-dark cart-modal" style={{
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                borderRadius: '1.5rem 1.5rem 0 0',
                padding: '1.5rem',
                overflowY: 'auto'
            }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>{t('cart.title')}</h2>
                    <button
                        onClick={onClose}
                        aria-label="Close cart"
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            color: 'white',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        ×
                    </button>
                </div>

                {cart.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {t('cart.empty')}
                    </div>
                ) : (
                    <>
                        {/* Cart Items */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            {cart.map((item, index) => (
                                <div key={item.lineId || `${item.product_id}-${index}`} className="glass" style={{
                                    padding: '1rem',
                                    borderRadius: '0.75rem',
                                    marginBottom: '0.75rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.5rem'
                                }}>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ color: 'white', marginBottom: '0.25rem' }}>{item.name}</h4>
                                            <p style={{ color: '#FFD700', fontSize: '0.875rem' }}>
                                                {Math.round(item.price + Number(item.extraPricePerUnit || 0))} {currency}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <button
                                                onClick={() => onUpdateQuantity(item.lineId, item.quantity - 1)}
                                                aria-label="Decrease quantity"
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.1)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    color: 'white',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                -
                                            </button>
                                            <span style={{ color: 'white', minWidth: '20px', textAlign: 'center' }}>{item.quantity}</span>
                                            <button
                                                onClick={() => onUpdateQuantity(item.lineId, item.quantity + 1)}
                                                aria-label="Increase quantity"
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.1)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    color: 'white',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                +
                                            </button>
                                            <button
                                                onClick={() => onRemove(item.lineId)}
                                                aria-label="Remove item"
                                                style={{
                                                    background: 'rgba(139, 0, 0, 0.5)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    color: 'white',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>

                                    {/* Display Build-Your-Own selections */}
                                    {item.selectedOptions && item.selectedOptions.length > 0 && (() => {
                                        const grouped: Record<string, string[]> = {};
                                        item.selectedOptions.forEach((opt) => {
                                            if (!grouped[opt.group_name]) grouped[opt.group_name] = [];
                                            const label = opt.extra_price > 0
                                                ? `${opt.option_label} (+${Math.round(opt.extra_price)} ${currency})`
                                                : opt.option_label;
                                            grouped[opt.group_name].push(label);
                                        });
                                        return (
                                            <div style={{
                                                marginTop: '0.5rem',
                                                paddingTop: '0.5rem',
                                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                                fontSize: '0.75rem',
                                                color: 'rgba(255, 215, 0, 0.85)'
                                            }}>
                                                {Object.entries(grouped).map(([gname, labels]) => (
                                                    <div key={gname} style={{ marginBottom: '0.2rem' }}>
                                                        <strong>{gname}:</strong> {labels.join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}

                                    {/* Display Customizations */}
                                    {item.customizations && (
                                        <div style={{
                                            marginTop: '0.5rem',
                                            paddingTop: '0.5rem',
                                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                            fontSize: '0.75rem',
                                            color: 'rgba(255, 255, 255, 0.7)'
                                        }}>
                                            {item.customizations.removed_ingredients && item.customizations.removed_ingredients.length > 0 && (
                                                <div style={{ marginBottom: '0.25rem' }}>
                                                    <strong>No:</strong> {item.customizations.removed_ingredients.join(', ')}
                                                </div>
                                            )}
                                            {item.customizations.selected_accompaniment && (
                                                <div style={{ marginBottom: '0.25rem' }}>
                                                    <strong>With:</strong> {item.customizations.selected_accompaniment}
                                                </div>
                                            )}
                                            {item.customizations.special_notes && (
                                                <div>
                                                    <strong>Note:</strong> {item.customizations.special_notes}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Customer Info */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="customerName"
                                style={{ display: 'block', color: 'white', marginBottom: '0.5rem', fontSize: '0.875rem' }}
                            >
                                Your Name *
                            </label>
                            <input
                                id="customerName"
                                type="text"
                                value={customerName}
                                onChange={(e) => {
                                    setCustomerName(e.target.value);
                                    if (nameError && e.target.value.trim()) {
                                        setNameError('');
                                    }
                                }}
                                placeholder="Enter your name"
                                aria-required="true"
                                aria-invalid={!!nameError}
                                aria-describedby={nameError ? "nameError" : undefined}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    border: nameError
                                        ? '1px solid rgba(239, 68, 68, 0.7)'
                                        : '1px solid rgba(255, 255, 255, 0.1)',
                                    background: nameError
                                        ? 'rgba(239, 68, 68, 0.1)'
                                        : 'rgba(255, 255, 255, 0.05)',
                                    color: 'white',
                                    fontSize: '1rem',
                                    transition: 'border-color 0.2s, background 0.2s'
                                }}
                            />
                            {nameError && (
                                <p
                                    id="nameError"
                                    role="alert"
                                    style={{
                                        color: '#ef4444',
                                        fontSize: '0.75rem',
                                        marginTop: '0.5rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                    }}
                                >
                                    <span>⚠</span> {nameError}
                                </p>
                            )}
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label
                                htmlFor="specialNotes"
                                style={{ display: 'block', color: 'white', marginBottom: '0.5rem', fontSize: '0.875rem' }}
                            >
                                Special Instructions
                            </label>
                            <textarea
                                id="specialNotes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Any special requests?"
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: 'white',
                                    fontSize: '1rem',
                                    resize: 'none'
                                }}
                            />
                        </div>

                        {/* Order Summary */}
                        <div className="glass" style={{ padding: '1rem', borderRadius: '0.75rem', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                <span>Subtotal</span>
                                <span>{Math.round(subtotal)} {currency}</span>
                            </div>
                            {Number(establishment?.tax_rate || 0) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                    <span>Tax ({establishment?.tax_rate}%)</span>
                                    <span>{Math.round(tax)} {currency}</span>
                                </div>
                            )}
                            {Number(establishment?.service_charge || 0) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                    <span>Service ({establishment?.service_charge}%)</span>
                                    <span>{Math.round(service)} {currency}</span>
                                </div>
                            )}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: '0.75rem',
                                paddingTop: '0.75rem',
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                color: 'white',
                                fontSize: '1.25rem',
                                fontWeight: 'bold'
                            }}>
                                <span>{t('cart.total')}</span>
                                <span>{Math.round(total)} {currency}</span>
                            </div>
                        </div>

                        {/* Place Order Button */}
                        <button
                            onClick={handleSubmitOrder}
                            disabled={isSubmitting}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                borderRadius: '0.75rem',
                                border: 'none',
                                background: isSubmitting ? 'rgba(139, 0, 0, 0.5)' : 'rgba(139, 0, 0, 0.9)',
                                color: 'white',
                                fontSize: '1.125rem',
                                fontWeight: 'bold',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s'
                            }}
                        >
                            {isSubmitting ? t('common.loading') : t('cart.placeOrder')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
});

CartModal.displayName = 'CartModal';

export default CartModal;
